import React, { useState, useEffect } from "react";
import BaseEmailComponent from "../BaseEmailComponent";
import EmailImageUpload from "../EmailImageUpload";

const RightAlignImage = ({ id, data, onUpdate, styleSettings, previewMode }) => {
  // Extract data with fallbacks
  const extractData = () => {
    const title = data?.title || data?.heading || "";
    const description = data?.description || data?.text || data?.content || "";
    const image = data?.image || data?.img || data?.src || null;
    const buttonText = data?.button_text || data?.cta || "";
    const buttonLink = data?.button_link || data?.url || data?.href || "#";
    const imageWidth = data?.image_width || data?.imgWidth || "40%";
    
    return { title, description, image, buttonText, buttonLink, imageWidth };
  };

  // Extract initial values
  const { 
    title: initialTitle,
    description: initialDescription,
    image: initialImage,
    buttonText: initialButtonText,
    buttonLink: initialButtonLink,
    imageWidth: initialImageWidth
  } = extractData();
  
  // State hooks
  const [title, setTitle] = useState(initialTitle);
  const [description, setDescription] = useState(initialDescription);
  const [contentImage, setContentImage] = useState(initialImage);
  const [ctaText, setCtaText] = useState(initialButtonText);
  const [ctaLink, setCtaLink] = useState(initialButtonLink);
  const [imgWidth, setImgWidth] = useState(initialImageWidth);
  const [isUpdatingImage, setIsUpdatingImage] = useState(false);

  // Update local state when props change
  useEffect(() => {
    if (!isUpdatingImage) {
      const { title, description, image, buttonText, buttonLink, imageWidth } = extractData();
      setTitle(title);
      setDescription(description);
      setContentImage(image);
      setCtaText(buttonText);
      setCtaLink(buttonLink);
      setImgWidth(imageWidth);
    }
  }, [data, isUpdatingImage]);

  // Handle image upload
  const handleImageUpload = (newImage) => {
    console.log("RightAlignImage: Image upload handler called with new image");
    setIsUpdatingImage(true);
    setContentImage(newImage);
    
    // Use setTimeout to ensure state is updated before triggering props update
    setTimeout(() => {
      if (onUpdate) {
        onUpdate({
          ...data,
          component_name: "2Col_1Img_right",
          title,
          description,
          image: newImage,
          button_text: ctaText,
          button_link: ctaLink,
          image_width: imgWidth
        }, id);
      }
      
      // Reset updating flag after a short delay to prevent race conditions
      setTimeout(() => setIsUpdatingImage(false), 300);
    }, 0);
  };

  // Handle settings update
  const handleSettingsUpdate = () => {
    if (onUpdate) {
      onUpdate({
        ...data,
        component_name: "2Col_1Img_right",
        title,
        description,
        image: contentImage,
        button_text: ctaText,
        button_link: ctaLink,
        image_width: imgWidth
      }, id);
    }
  };

  // Edit UI
  const renderEditUI = (props) => {
    const { title: propsTitle, setTitle: propsSetTitle, description: propsDescription, setDescription: propsSetDescription, handleSaveChanges: propsSaveChanges } = props;
    
    return (
      <div className="p-3 rounded bg-white">
        <div className="mb-3">
          <label className="form-label fw-bold">Title</label>
          <input
            type="text"
            className="form-control"
            value={propsTitle}
            onChange={(e) => propsSetTitle(e.target.value)}
            onBlur={() => propsSaveChanges(propsTitle, propsDescription)}
          />
        </div>
        <div className="mb-3">
          <label className="form-label fw-bold">Content</label>
          <textarea
            className="form-control"
            rows="3"
            value={propsDescription}
            onChange={(e) => propsSetDescription(e.target.value)}
            onBlur={() => propsSaveChanges(propsTitle, propsDescription)}
          />
        </div>
        <div className="mb-3">
          <label className="form-label fw-bold">Image</label>
          <EmailImageUpload
            initialImage={contentImage}
            onImageUpload={handleImageUpload}
            height="200px"
            placeholderText="Upload an image"
            allowRegenerate={true}
            onRegenerateClick={() => console.log("Regenerate image clicked - not implemented")}
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Image Width</label>
          <select
            className="form-select"
            value={imgWidth}
            onChange={(e) => {
              setImgWidth(e.target.value);
              setTimeout(handleSettingsUpdate, 0);
            }}
          >
            <option value="30%">Small (30%)</option>
            <option value="40%">Medium (40%)</option>
            <option value="50%">Large (50%)</option>
          </select>
        </div>
        <div className="mb-3">
          <label className="form-label fw-bold">Button Text</label>
          <input
            type="text"
            className="form-control"
            value={ctaText}
            onChange={(e) => {
              setCtaText(e.target.value);
              setTimeout(handleSettingsUpdate, 0);
            }}
            placeholder="Leave empty for no button"
          />
        </div>
        {ctaText && (
          <div className="mb-3">
            <label className="form-label fw-bold">Button Link</label>
            <input
              type="text"
              className="form-control"
              value={ctaLink}
              onChange={(e) => {
                setCtaLink(e.target.value);
                setTimeout(handleSettingsUpdate, 0);
              }}
              placeholder="https://"
            />
          </div>
        )}
      </div>
    );
  };

  // View UI
  const renderViewUI = (props) => {
    const { title: propsTitle, description: propsDescription, styleSettings } = props;
    const { primaryColor, secondaryColor, fontFamily } = styleSettings || {};
    
    return (
      <div 
        className="right-align-image py-3" 
        style={{ fontFamily }}
      >
        <div className="row align-items-center ">
          <div className="content-container  flex-fill">
            <h3 
              className="mb-3" 
              style={{ 
                color: secondaryColor || "#333333",
                fontWeight: "600" 
              }}
            >
              {propsTitle || title}
            </h3>
            <div 
              className="mb-3"
              style={{ color: primaryColor || "#555555", lineHeight: 1.6 }}
              dangerouslySetInnerHTML={{ __html: (() => {
                const desc = propsDescription || description;
                if (typeof desc === 'string') {
                  return desc.replace(/\n/g, '<br/>');
                } else if (Array.isArray(desc)) {
                  return desc.join('\n').replace(/\n/g, '<br/>');
                } else {
                  return String(desc || '').replace(/\n/g, '<br/>');
                }
              })() }}
            />
            {ctaText && (
              <a
                href={ctaLink}
                className="btn d-inline-block px-4 py-2 text-white"
                style={{
                  backgroundColor: primaryColor || "#007bff",
                  borderRadius: "4px",
                  textDecoration: "none",
                  fontWeight: "500"
                }}
                target="_blank"
                rel="noopener noreferrer"
              >
                {ctaText}
              </a>
            )}
          </div>
          <div className="image-container " >
            {contentImage ? (
              <img 
                src={contentImage} 
                alt={propsTitle || title || "Image"} 
                className="img-fluid rounded" 
                style={{ width: "100%" }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://via.placeholder.com/400x300/e0e0e0/999999?text=Image+Error";
                }}
              />
            ) : (
              <div 
                className="placeholder-image bg-light rounded d-flex align-items-center justify-content-center" 
                style={{ height: "200px", border: "1px dashed #ccc" }}
              >
                <div className="text-center text-muted">
                  <p className="mb-0">Image</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <BaseEmailComponent
      id={id}
      data={{
        ...data,
        component_name: "2Col_1Img_right",
        title,
        description,
        image: contentImage,
        button_text: ctaText,
        button_link: ctaLink,
        image_width: imgWidth
      }}
      onUpdate={onUpdate}
      componentName="Right Image with Content"
      styleSettings={styleSettings}
      editUI={renderEditUI}
      viewUI={renderViewUI}
      previewMode={previewMode}
    />
  );
};

export default RightAlignImage;
